import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, ImagePlus, Newspaper, X, Save, Upload, Link2, ExternalLink, Send, Radio, CheckCircle2, AlertCircle, Zap } from 'lucide-react';
import {
  listCompanyNews,
  createCompanyNews,
  updateCompanyNews,
  deleteCompanyNews,
  broadcastCompanyNews,
  zapierTest,
  zapierLog,
  uploadFile,
  resolveFileUrl,
} from '../../services/api';
import { toast } from '../../hooks/use-toast';

const empty = {
  title: '',
  slug: '',
  short_desc: '',
  long_desc: '',
  image_url: '',
  external_mentions: [],
  published_at: '',
  active: true,
};

const CompanyNewsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCompanyNews(100);
      setItems(res.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...empty, published_at: new Date().toISOString().slice(0, 10) });
    setDrawerOpen(true);
  };

  const openEdit = (n) => {
    setEditing(n);
    setForm({
      title: n.title,
      slug: n.slug,
      short_desc: n.short_desc,
      long_desc: n.long_desc,
      image_url: n.image_url || '',
      external_mentions: n.external_mentions || [],
      published_at: (n.published_at || '').slice(0, 10),
      active: n.active !== false,
    });
    setDrawerOpen(true);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const res = await uploadFile(file, setProgress);
      setForm((f) => ({ ...f, image_url: res.url }));
      toast({ title: 'Image uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', variant: 'destructive', description: err?.response?.data?.detail || 'Retry' });
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.published_at && payload.published_at.length === 10) {
        payload.published_at = new Date(payload.published_at).toISOString();
      }
      if (editing) {
        await updateCompanyNews(editing.id, payload);
      } else {
        await createCompanyNews(payload);
      }
      toast({ title: editing ? 'News updated' : 'News published' });
      setDrawerOpen(false);
      load();
    } catch (err) {
      toast({ title: 'Save failed', variant: 'destructive', description: err?.response?.data?.detail || 'Check the form' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Delete "${n.title}"?`)) return;
    try {
      await deleteCompanyNews(n.id);
      load();
    } catch {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const addMention = () => setForm((f) => ({ ...f, external_mentions: [...f.external_mentions, { title: '', url: '', source: '' }] }));
  const updateMention = (i, k, v) => setForm((f) => ({
    ...f,
    external_mentions: f.external_mentions.map((m, idx) => idx === i ? { ...m, [k]: v } : m),
  }));
  const removeMention = (i) => setForm((f) => ({
    ...f,
    external_mentions: f.external_mentions.filter((_, idx) => idx !== i),
  }));

  return (
    <div>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">CMS</span>
          <h1 className="mt-2 text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Company News</h1>
          <p className="mt-2 text-sm text-[#0b1a2e]/60">Publish Emerald press releases and add external media mentions.</p>
        </div>
        <button onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0f5c3d] text-white text-sm font-medium hover:bg-[#0b1a2e] transition">
          <Plus className="w-4 h-4" /> New Article
        </button>
      </div>

      <ZapierPanel />
      {loading ? (
        <div className="bg-white border border-black/5 rounded-2xl p-10 text-center text-[#0b1a2e]/60">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-black/5 rounded-2xl p-10 text-center">
          <Newspaper className="w-10 h-10 mx-auto text-[#0b1a2e]/30 mb-3" />
          <p className="text-[#0b1a2e]/60 mb-4">No news yet.</p>
          <button onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm">
            <Plus className="w-4 h-4" /> Publish first article
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((n) => (
            <div key={n.id} className="bg-white border border-black/5 rounded-2xl overflow-hidden hover:shadow-md transition">
              <div className="aspect-[16/10] bg-[#e2ede6] overflow-hidden">
                {n.image_url ? (
                  <img src={resolveFileUrl(n.image_url)} alt={n.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="flex items-center justify-center h-full text-[#0b1a2e]/30"><ImagePlus className="w-8 h-8" /></div>
                )}
              </div>
              <div className="p-5">
                <div className="text-xs text-[#0b1a2e]/60">{n.published_at?.slice(0, 10)}</div>
                <h3 className="mt-1 font-semibold text-[#0b1a2e] line-clamp-2">{n.title}</h3>
                <p className="mt-2 text-sm text-[#0b1a2e]/60 line-clamp-2">{n.short_desc}</p>
                {n.external_mentions?.length > 0 && (
                  <div className="mt-2 text-xs text-[#0f5c3d] inline-flex items-center gap-1"><Link2 className="w-3 h-3" /> {n.external_mentions.length} mention{n.external_mentions.length === 1 ? '' : 's'}</div>
                )}
                <div className="mt-4 flex gap-2">
                  <button onClick={() => openEdit(n)} className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg border border-[#0f5c3d]/20 text-xs font-medium hover:bg-[#f7fbf8]"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                  <button
                    onClick={async () => {
                      try { await broadcastCompanyNews(n.id); toast({ title: 'Re-broadcast queued', description: 'Zapier will fan it out to LinkedIn / X / Facebook.' }); }
                      catch (e) { toast({ title: e?.response?.data?.detail || 'Broadcast failed', variant: 'destructive' }); }
                    }}
                    data-testid={`broadcast-${n.id}`}
                    title="Re-broadcast this post via Zapier"
                    className="inline-flex items-center justify-center px-3 py-2 rounded-lg text-[#0f5c3d] hover:bg-[#f7fbf8]"
                  >
                    <Radio className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(n)} className="inline-flex items-center justify-center px-3 py-2 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {drawerOpen && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <form onSubmit={save} className="relative bg-white w-full sm:max-w-2xl h-full overflow-y-auto shadow-2xl flex flex-col">
            <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#0f5c3d] font-semibold">{editing ? 'Edit article' : 'New article'}</div>
                <div className="text-lg font-semibold text-[#0b1a2e]">{form.title || 'Untitled'}</div>
              </div>
              <button type="button" onClick={() => setDrawerOpen(false)} className="p-2 hover:bg-black/5 rounded-lg"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-6 space-y-5 flex-1">
              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Title</span>
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30" />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Slug</span>
                  <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto if empty" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30" />
                </label>
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Publish date</span>
                  <input type="date" value={form.published_at} onChange={(e) => setForm({ ...form, published_at: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30" />
                </label>
              </div>
              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Short description</span>
                <textarea required rows={2} value={form.short_desc} onChange={(e) => setForm({ ...form, short_desc: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30 resize-y" />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Long description</span>
                <textarea required rows={8} value={form.long_desc} onChange={(e) => setForm({ ...form, long_desc: e.target.value })} className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30 resize-y" />
              </label>

              <div>
                <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Featured image</span>
                <div className="flex items-start gap-4">
                  <div className="w-40 h-28 rounded-lg overflow-hidden bg-[#e2ede6] flex items-center justify-center shrink-0">
                    {form.image_url ? <img src={resolveFileUrl(form.image_url)} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="w-6 h-6 text-[#0b1a2e]/30" />}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} />
                    <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#0f5c3d]/20 text-sm hover:bg-[#f7fbf8] disabled:opacity-60">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
                    </button>
                    {uploading && <div className="w-full h-1.5 bg-[#e2ede6] rounded-full overflow-hidden"><div className="h-full bg-[#0f5c3d]" style={{ width: `${progress}%` }} /></div>}
                    <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="or paste URL" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-xs outline-none" />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70">Media mentions & external links</span>
                  <button type="button" onClick={addMention} className="text-xs font-medium text-[#0f5c3d] hover:underline">+ Add link</button>
                </div>
                <div className="space-y-2">
                  {form.external_mentions.length === 0 ? (
                    <p className="text-xs text-[#0b1a2e]/50">Add links to press coverage on external sites.</p>
                  ) : form.external_mentions.map((m, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-start bg-[#f7fbf8] p-2 rounded-lg">
                      <input value={m.title} onChange={(e) => updateMention(i, 'title', e.target.value)} placeholder="Headline as it appeared" className="col-span-5 border border-[#0f5c3d]/15 rounded px-2 py-1.5 text-xs outline-none" />
                      <input value={m.source || ''} onChange={(e) => updateMention(i, 'source', e.target.value)} placeholder="Source (e.g., Reuters)" className="col-span-3 border border-[#0f5c3d]/15 rounded px-2 py-1.5 text-xs outline-none" />
                      <input value={m.url} onChange={(e) => updateMention(i, 'url', e.target.value)} placeholder="https://…" className="col-span-3 border border-[#0f5c3d]/15 rounded px-2 py-1.5 text-xs outline-none" />
                      <button type="button" onClick={() => removeMention(i)} className="col-span-1 text-red-500 hover:text-red-700 text-xs"><Trash2 className="w-3.5 h-3.5 mx-auto" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-3">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-[#0f5c3d]" />
                <span className="text-sm text-[#0b1a2e]">Published (visible on public site)</span>
              </label>
            </div>

            <div className="px-6 py-4 border-t border-black/5 bg-[#f7fbf8] flex items-center justify-end gap-2">
              <button type="button" onClick={() => setDrawerOpen(false)} className="px-4 py-2 rounded-lg text-sm text-[#0b1a2e]/70 hover:bg-black/5">Cancel</button>
              <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0f5c3d] text-white text-sm font-medium hover:bg-[#0b1a2e] transition disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editing ? 'Save changes' : 'Publish article'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default CompanyNewsPage;

const ZapierPanel = () => {
  const [log, setLog] = useState(null);
  const [testing, setTesting] = useState(false);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try { setLog(await zapierLog(10)); } catch (_) { /* silent */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const fireTest = async () => {
    setTesting(true);
    try {
      await zapierTest();
      toast({ title: 'Test payload sent to Zapier', description: 'Head to your Zap → click "Test trigger" — it should show the fields now.' });
      setTimeout(refresh, 1500);
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Test failed', variant: 'destructive' });
    } finally { setTesting(false); }
  };

  if (!log) return null;
  const configured = log.configured;
  const lastOk = log.data?.[0]?.ok;

  return (
    <div className="mb-8 bg-white border border-black/5 rounded-2xl overflow-hidden" data-testid="zapier-panel">
      <div className="p-4 flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center flex-shrink-0">
          <Zap className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs font-semibold tracking-widest uppercase text-orange-600">Zapier auto-broadcast</div>
          <div className="text-sm text-[#0b1a2e]">
            {configured
              ? <>Every new post fires to your Zap → LinkedIn / X / Facebook{lastOk === true && <span className="ml-1 text-emerald-700">· last delivery ✓</span>}{lastOk === false && <span className="ml-1 text-red-600">· last delivery failed</span>}</>
              : <span className="text-amber-700">Not configured — add <code className="text-xs">ZAPIER_POSTS_WEBHOOK_URL</code> to backend .env</span>
            }
          </div>
        </div>
        {configured && (
          <>
            <button onClick={fireTest} disabled={testing} data-testid="zapier-send-test"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-60">
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send test payload
            </button>
            <button onClick={() => setOpen((o) => !o)} data-testid="zapier-toggle-log"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#0f5c3d]/25 text-[#0f5c3d] text-xs font-semibold hover:bg-[#f7fbf8]">
              {open ? 'Hide' : 'View'} delivery log ({log.data?.length || 0})
            </button>
          </>
        )}
      </div>
      {open && configured && (
        <div className="border-t border-black/5 max-h-64 overflow-y-auto">
          {(log.data || []).length === 0 && <div className="p-4 text-xs text-[#0b1a2e]/50">No deliveries yet.</div>}
          {(log.data || []).map((d, i) => (
            <div key={i} className="px-4 py-2 border-b border-black/5 text-xs flex items-center gap-2 flex-wrap">
              {d.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <AlertCircle className="w-3.5 h-3.5 text-red-600" />}
              <span className="font-medium">{d.event}</span>
              <span className="text-[#0b1a2e]/60">{d.post_title || d.post_id}</span>
              <span className="text-[#0b1a2e]/50 ml-auto">{new Date(d.created_at).toLocaleString()}</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${d.ok ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                {d.status_code || d.error || '—'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
